import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Switch,
  Text,
} from "@chakra-ui/react";
import {
  HiOutlineArrowRight,
  HiOutlineEllipsisHorizontal,
  HiOutlinePencilSquare,
  HiOutlineTrash,
  HiOutlineXMark,
} from "react-icons/hi2";
import { c, PALETTES } from "../classroomColors";
import { PreClassTeacherPanel } from "../pre-class/PreClassTeacherPanel";
import { PreClassStudentPanel } from "../pre-class/PreClassStudentPanel";
import SessionAssignments from "../sessions/SessionAssignments";
import SessionRecap from "./SessionRecap";
import { StateChip, blueButton, inkButton } from "./parts";
import { clock, dayLabel, plural, questionsLine, whenLine } from "./spaceTokens";

/** Where a session's sheet opens: on what matters for where it is now. */
const firstStep = (session) => {
  if (session.state === "DONE") return "after";
  if (session.state === "LIVE") return "live";
  return session.questions_enabled ? "before" : "live";
};

const Step = ({ id, kicker, title, detail, active, onClick }) => (
  <Box
    as="button"
    type="button"
    role="tab"
    aria-selected={active}
    id={`step-${id}`}
    onClick={onClick}
    textAlign="left"
    p="12px"
    borderRadius="14px"
    bg={c.surface}
    border="1px solid"
    borderColor={active ? c.text : c.borderSoft}
    boxShadow={active ? `0 0 0 1px ${c.text}` : "none"}
    transition="border-color 0.15s"
    _hover={active ? {} : { borderColor: c.textMuted }}
    minW={0}
  >
    <Text fontSize="10.5px" letterSpacing="0.1em" textTransform="uppercase" color={c.textMuted} fontWeight="600">
      {kicker}
    </Text>
    <Text fontSize="13px" fontWeight="600" color={c.text}>
      {title}
    </Text>
    <Text fontSize="12px" color={c.textSub} noOfLines={1}>
      {detail}
    </Text>
  </Box>
);

/**
 * One session, opened over the space home (so the home is never lost).
 *
 * Its three parts as steps in time — Before (questions, if the session
 * takes them), Live (the room), After (what the room ran, and the work set
 * after it) — each saying where it stands. It opens on the part that
 * matters now. The parts reuse what already exists: the pre-class panels,
 * the room, the session's assignments.
 *
 * The drawer is portalled out of the page, so its content carries the
 * classroom theme itself, for the panels inside to keep their colours.
 */
const SessionSheet = ({
  session,
  isOpen,
  onClose,
  courseGuid,
  subjectGuid,
  spaceName,
  isInstructor,
  isDark,
  onEdit,
  onDelete,
  onEnter,
  onToggleQuestions,
  onChanged,
}) => {
  const [step, setStep] = useState("before");
  useEffect(() => {
    if (session) setStep(firstStep(session));
    // Only when a different session opens, not on every refresh of it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.guid]);
  const p = isDark ? PALETTES.dark : PALETTES.light;

  if (!session) return null;
  const isLive = session.state === "LIVE";
  const isDone = session.state === "DONE";

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay bg={p.overlay} />
      <DrawerContent className="stande-classroom" data-theme={isDark ? "dark" : "light"} bg={c.page} maxW="560px">
        <DrawerBody p="22px" display="flex" flexDirection="column" gap="16px">
          <Flex justify="space-between" align="flex-start" gap="12px">
            <Box minW={0}>
              <Flex align="center" gap="10px" wrap="wrap" mb="6px">
                <StateChip state={session.state} isDark={isDark} />
                <Text fontSize="12.5px" color={c.textSub}>{whenLine(session) || "No date yet"}</Text>
              </Flex>
              <Text fontSize="20px" fontWeight="600" color={c.text} letterSpacing="-0.01em">
                {session.name}
              </Text>
            </Box>
            <Flex gap="4px" flexShrink={0}>
              {isInstructor && (
                <Menu placement="bottom-end" autoSelect={false}>
                  <MenuButton as={IconButton} aria-label={`Options for ${session.name}`} icon={<Icon as={HiOutlineEllipsisHorizontal} boxSize="18px" />}
                    variant="ghost" size="sm" borderRadius="9px" color={c.textSub} _hover={{ bg: c.hover, color: c.text }} />
                  <MenuList minW="180px" py="6px" bg={p.surface} color={p.text} borderColor={p.border} borderRadius="12px" fontSize="13px" zIndex={1500}>
                    <MenuItem icon={<Icon as={HiOutlinePencilSquare} boxSize="16px" />} bg="transparent" _hover={{ bg: p.hover }} onClick={() => onEdit(session)}>
                      Edit session
                    </MenuItem>
                    <MenuItem icon={<Icon as={HiOutlineTrash} boxSize="16px" />} bg="transparent" color={p.accentSoftText} _hover={{ bg: p.hover }} onClick={() => onDelete(session)}>
                      Delete session
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
              <IconButton aria-label="Close" icon={<Icon as={HiOutlineXMark} boxSize="18px" />} variant="ghost" size="sm" borderRadius="9px"
                color={c.textSub} _hover={{ bg: c.hover, color: c.text }} onClick={onClose} />
            </Flex>
          </Flex>

          <Box role="tablist" aria-label="Parts of the session" display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap="8px">
            <Step id="before" kicker="Before" title="Questions" active={step === "before"} onClick={() => setStep("before")}
              detail={questionsLine(session, isInstructor)} />
            <Step id="live" kicker="Live" title="The room" active={step === "live"} onClick={() => setStep("live")}
              detail={isLive ? "Open now" : isDone ? (session.attended_count ? `${plural(session.attended_count, "student")} came` : "Held") : session.scheduled_at ? `Opens ${dayLabel(session.scheduled_at)}` : "Not scheduled"} />
            <Step id="after" kicker="After" title="Recap and work" active={step === "after"} onClick={() => setStep("after")}
              detail={[session.activity_count ? plural(session.activity_count, "activity", "activities") : null, session.assignment_count ? plural(session.assignment_count, "task") : null].filter(Boolean).join(" · ") || "Nothing yet"} />
          </Box>

          <Box role="tabpanel" aria-labelledby={`step-${step}`}>
            {step === "before" && (
              <Box>
                {isInstructor && (
                  <Flex as="label" gap="12px" align="flex-start" p="12px 14px" mb="12px" borderRadius="14px" bg={c.surface} border="1px solid" borderColor={c.borderSoft} cursor="pointer">
                    <Switch isChecked={session.questions_enabled} onChange={(e) => onToggleQuestions(session, e.target.checked)} mt="2px"
                      sx={{ "span.chakra-switch__track": { bg: c.border }, "span.chakra-switch__track[data-checked]": { bg: c.solidBg }, "span.chakra-switch__thumb": { bg: c.page } }} />
                    <Box>
                      <Text fontSize="13.5px" fontWeight="600" color={c.text}>Questions before class</Text>
                      <Text fontSize="12.5px" color={c.textSub}>
                        {session.questions_enabled ? "Students can send what they want covered." : "Switched off. Students can't send questions for this session."}
                      </Text>
                    </Box>
                  </Flex>
                )}
                {session.questions_enabled ? (
                  isInstructor ? (
                    <PreClassTeacherPanel courseGuid={courseGuid} segmentGuid={session.guid} bare onChanged={onChanged} />
                  ) : (
                    <PreClassStudentPanel courseGuid={courseGuid} segmentGuid={session.guid} bare onChanged={onChanged} />
                  )
                ) : (
                  !isInstructor && <Text fontSize="13px" color={c.textSub}>This session isn't taking questions beforehand.</Text>
                )}
              </Box>
            )}

            {step === "live" && (
              <Flex direction="column" align="center" textAlign="center" gap="10px" py="28px" px="10px" borderRadius="16px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
                <StateChip state={session.state} isDark={isDark} />
                <Text fontSize="16px" fontWeight="600" color={c.text}>
                  {isLive ? "The room is open" : isDone ? "This session has ended" : "The room for this session"}
                </Text>
                <Text fontSize="13px" color={c.textSub} maxW="380px">
                  {isLive
                    ? `Opened at ${clock(session.started_at)}. The board, the chat and who is here.`
                    : isDone
                    ? "The board stays as it was left. Its recap is under After."
                    : isInstructor
                    ? "Open the room to get it ready or try it out. Nothing goes live until you press Go live inside it."
                    : "Your teacher opens the room when the class starts."}
                </Text>
                {(isInstructor || isLive || isDone) && (
                  <Button {...(isLive ? blueButton : inkButton)} mt="4px" h="40px" px="16px" borderRadius="12px" fontSize="13.5px"
                    rightIcon={<Icon as={HiOutlineArrowRight} boxSize="14px" />} onClick={() => onEnter(session)}>
                    {isLive ? (isInstructor ? "Enter room" : "Join the room") : isDone ? "Open the board" : "Open room"}
                  </Button>
                )}
              </Flex>
            )}

            {step === "after" && (
              <Flex direction="column" gap="18px">
                <SessionRecap session={session} isInstructor={isInstructor} />
                <Box>
                  <Text fontSize="13.5px" fontWeight="600" color={c.text} mb="6px">Follow-up work</Text>
                  <SessionAssignments session={session} courseGuid={courseGuid} subjectGuid={subjectGuid}
                    classroomName={spaceName} isInstructor={isInstructor} isDark={isDark} />
                </Box>
              </Flex>
            )}
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default SessionSheet;
